import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FileCode2,
  FileJson,
  FileLock2,
  FileKey2,
  Download,
  Copy,
  ShieldCheck,
  KeyRound,
  Clock,
  Hash,
  CalendarCheck2,
  ChevronLeft,
  Check,
  Fingerprint,
} from "lucide-react";
import { API_URL } from "../config";
import { downloadWithAuth } from "../utils/api";
import { SIGN_SCRIPT, buildTemplate } from "../utils/ciTemplates";

/* ---------------------------------------------------------
   Static data — only things that don't depend on a specific
   signed artifact (platform picker for the generated snippet,
   syntax colors). Everything about THIS artifact's signing is
   built from location.state further down.
--------------------------------------------------------- */

const TOP_TABS = ["CI/CD Pipelines", "OTA Deployment", "Downloads", "REST API"];

// Only platforms that have a real, tested-in-structure template in ci/.
const PLATFORMS = [
  { id: "jenkins", label: "Jenkins", emoji: "🤵", bg: "#F1F2F6" },
  { id: "github", label: "GitHub Actions", emoji: "🐙", bg: "#EDEBFF" },
  { id: "gitlab", label: "GitLab CI", emoji: "🦊", bg: "#FFF1EC" },
  { id: "azure", label: "Azure DevOps", emoji: "🔷", bg: "#EAF2FE" },
];

// The shared signing script, one ready-made template per platform, and a curl
// walk-through of the real endpoints. Nothing here is illustrative only.
const CODE_TABS = ["Pipeline template", "Signing script", "REST API (curl)"];

/* ---------------------------------------------------------
   Helpers (pure functions)
--------------------------------------------------------- */

// File formats depend on the Digital ID's profile: raw bytes for
// post-quantum, PEM for ECDSA, JSON for hybrid.
const SIGNATURE_NAMES = { hybrid: "signature.json" };
const PUBLIC_KEY_NAMES = { hybrid: "public_key.json", pq: "public_key.bin" };

const fileNamesFor = (identityType) => {
  const type = String(identityType || "").toLowerCase();
  return {
    signature: SIGNATURE_NAMES[type] || "signature.sig",
    publicKey: PUBLIC_KEY_NAMES[type] || "public_key.pem",
  };
};

// Real, downloadable files only. The previous version listed sbom.json,
// cbom.json, certificate.pem, verification_report.pdf and audit_log.json
// - none of which this backend generates. Removed rather than marked
// pending, per the "don't list what doesn't exist" call.
const buildPackageFiles = ({ artifactId, manifest, fileName, names }) => [
  {
    name: `signed_${fileName}`,
    size: `${(manifest.firmware.file_size / 1024 / 1024).toFixed(2)} MB`,
    icon: FileCode2,
    color: "#2E7D32",
    path: `/api/artifact/download/${artifactId}`,
  },
  {
    name: "manifest.json",
    size: "Generated",
    icon: FileJson,
    color: "#E8A33D",
    path: `/api/artifact/download/manifest/${artifactId}`,
  },
  {
    name: names.signature,
    size: "Generated",
    icon: FileLock2,
    color: "#5B4FE0",
    path: `/api/artifact/download/signature/${artifactId}`,
  },
  {
    name: names.publicKey,
    size: "Generated",
    icon: FileKey2,
    color: "#2E9BE0",
    path: `/api/artifact/download/public-key/${artifactId}`,
  },
];

// "Requested", not "applied" - the backend records this flag but
// does not yet call a real Timestamp Authority.
const timestampLabel = (includeTSA, signResult) => {
  if (!includeTSA) return "Not requested";
  if (signResult?.timestamp) return `Internal timestamp applied (${signResult.timestamp})`;
  return "Requested";
};

const digitalIdLabel = (selectedDigitalId) => {
  if (selectedDigitalId?.identity_name) return selectedDigitalId.identity_name;
  if (selectedDigitalId?.id) return `#${selectedDigitalId.id}`;
  return "—";
};

// Only claims we can actually back with data from the sign response.
// Dropped "Certificate Valid", "Quantum Algorithm", "SBOM & CBOM
// Verified" - nothing in this flow checks any of those.
const buildSigningDetails = ({ selectedMethod, algorithm, hashAlgorithm, selectedDigitalId, includeTSA, signResult, signedAt }) => [
  { icon: ShieldCheck, label: "Identity Source", value: selectedMethod?.title || "—" },
  { icon: KeyRound, label: "Algorithm", value: algorithm || "—" },
  { icon: Hash, label: "Hash Algorithm", value: hashAlgorithm || "—" },
  { icon: Fingerprint, label: "Digital ID", value: digitalIdLabel(selectedDigitalId) },
  { icon: Clock, label: "Timestamp", value: timestampLabel(includeTSA, signResult) },
  { icon: CalendarCheck2, label: "Signed On", value: signedAt ? new Date(signedAt).toLocaleString() : "—" },
];

// Every request carries a token. A CI credential (ipq_ci_...) or a user login token both work.
const buildCurlSnippet = ({ apiUrl, fileName, artifactId, selectedDigitalId, includeTSA, anchorBlockchain }) => {
  const idValue = artifactId || "<artifact_id>";
  const digitalIdValue = selectedDigitalId?.id ?? "<digital_id>";
  return `# Set these in your shell or CI secret store. Never put the token in a file.
export IPQSIGN_API_URL="${apiUrl}"
export IPQSIGN_TOKEN="ipq_ci_..."

# 1. Upload the artifact
curl -sS -X POST "$IPQSIGN_API_URL/api/artifact/upload" \\
  -H "Authorization: Bearer $IPQSIGN_TOKEN" \\
  -F "file=@${fileName || "firmware.bin"}"

# 2. Generate the manifest (artifact_id from step 1)
curl -sS -X POST "$IPQSIGN_API_URL/api/artifact/generate-manifest" \\
  -H "Authorization: Bearer $IPQSIGN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"artifact_id": ${idValue}}'

# 3. Sign with a Digital ID your credential is allowed to use
curl -sS -X POST "$IPQSIGN_API_URL/api/artifact/sign" \\
  -H "Authorization: Bearer $IPQSIGN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "artifact_id": ${idValue},
    "digital_id": ${digitalIdValue},
    "include_tsa": ${String(!!includeTSA)},
    "blockchain_anchor": ${String(!!anchorBlockchain)}
  }'

# 4. Check the stored copy (signature_id from step 3). Step 6 checks the file you actually ship.
curl -sS -X POST "$IPQSIGN_API_URL/api/artifact/verify" \\
  -H "Authorization: Bearer $IPQSIGN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"artifact_id": ${idValue}, "signature_id": <signature_id>}'

# 5. Download the signed artifact
curl -sS -o signed_${fileName || "artifact.bin"} \\
  -H "Authorization: Bearer $IPQSIGN_TOKEN" \\
  "$IPQSIGN_API_URL/api/artifact/download/${idValue}"

# 6. Verify the exact file you are about to publish or deploy.
#    verify-file answers HTTP 200 even for FAILED and UNKNOWN, so read the JSON field.
#    Use the same credential that signed: it only matches artifacts it uploaded itself.
RESULT=$(curl -sS -X POST "$IPQSIGN_API_URL/api/artifact/verify-file" \\
  -H "Authorization: Bearer $IPQSIGN_TOKEN" \\
  -F "file=@signed_${fileName || "artifact.bin"}")

STATUS=$(echo "$RESULT" | jq -r '.verification_status')
if [ "$STATUS" != "VERIFIED" ]; then
  echo "Verification failed: $STATUS" >&2
  echo "$RESULT" | jq '{verification_status, hash_match, signature_valid, manifest_valid, trust_level, message}' >&2
  exit 3
fi
echo "Verified: the file matches what was signed. Safe to publish."`;
};

const TAB_ROUTES = {
  "OTA Deployment": "/ota-deployment",
  Downloads: "/downloads",
  "REST API": "/rest-api",
};

/* ---------------------------------------------------------
   Presentational components
--------------------------------------------------------- */

const TopTabs = ({ activeTab, onSelect }) => (
  <div style={styles.topTabs}>
    {TOP_TABS.map((tab) => {
      const active = activeTab === tab;
      return (
        <button
          key={tab}
          onClick={() => onSelect(tab)}
          style={{
            ...styles.topTabBtn,
            color: active ? "#5B4FE0" : "#8B92A8",
            borderBottom: active ? "2px solid #5B4FE0" : "2px solid transparent",
          }}
        >
          {tab}
        </button>
      );
    })}
  </div>
);

const PlatformCard = ({ activePlatform, onSelect }) => (
  <div style={styles.card}>
    <div style={styles.cardHeaderRow}>
      <div>
        <div style={styles.cardTitle}>CI/CD platform</div>
        <div style={styles.cardSubtitle}>
          Each platform has a ready-made template. All of them run the same signing
          script, which verifies the result and publishes nothing if verification fails.
        </div>
      </div>
    </div>

    <div style={styles.platformGrid}>
      {PLATFORMS.map((p) => {
        const active = p.id === activePlatform;
        return (
          <button
            key={p.id}
            onClick={() => onSelect(p.id)}
            style={{
              ...styles.platformTile,
              borderColor: active ? "#5B4FE0" : "#E8E9F1",
              boxShadow: active ? "0 0 0 3px rgba(91,79,224,0.12)" : "none",
            }}
          >
            {active && (
              <div style={styles.platformCheck}>
                <Check size={8} strokeWidth={3} color="#fff" />
              </div>
            )}
            <div style={{ ...styles.platformIcon, background: p.bg }}>{p.emoji}</div>
            <div style={styles.platformLabel}>{p.label}</div>
          </button>
        );
      })}
    </div>
  </div>
);

const SnippetCard = ({ activeCodeTab, onSelectCodeTab, snippet, onCopy, onCredentials }) => (
  <div style={styles.card}>
    <div style={styles.cardHeaderRow}>
      <div>
        <div style={styles.cardTitle}>Pipeline template and script</div>
        <div style={styles.cardSubtitle}>
          Needs a CI credential stored as the secret IPQSIGN_TOKEN.
        </div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button style={styles.secondaryBtn} onClick={onCredentials}>
          CI credentials
        </button>
        <button style={styles.secondaryBtn} onClick={onCopy}>
          <Copy size={14} /> Copy
        </button>
      </div>
    </div>

    <div style={styles.codeTabsRow}>
      {CODE_TABS.map((tab) => {
        const active = activeCodeTab === tab;
        return (
          <button
            key={tab}
            onClick={() => onSelectCodeTab(tab)}
            style={{
              ...styles.codeTabBtn,
              color: active ? "#5B4FE0" : "#8B92A8",
              borderBottom: active ? "2px solid #5B4FE0" : "2px solid transparent",
            }}
          >
            {tab}
          </button>
        );
      })}
    </div>

    <div style={styles.codeBlock}>
      <pre style={{ margin: 0, whiteSpace: "pre-wrap", color: "#EEFFFF" }}>{snippet}</pre>
    </div>
  </div>
);

const SigningDetailsCard = ({ details }) => (
  <div style={styles.card}>
    <div style={styles.cardTitle}>Signing Details</div>
    <div style={styles.signingGrid}>
      {details.map((m) => {
        const Icon = m.icon;
        return (
          <div key={m.label} style={styles.signingItem}>
            <div style={styles.signingIconWrap}>
              <Icon size={15} color="#5B4FE0" />
            </div>
            <div>
              <div style={styles.signingLabel}>{m.label}</div>
              <div style={styles.signingValue}>{m.value}</div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const PackageCard = ({ files, downloadError, onDownload, onDownloadAll }) => (
  <div style={styles.card}>
    <div style={styles.cardTitle}>Generated Package</div>
    <div style={styles.cardSubtitleGreen}>
      {files.length} real file{files.length === 1 ? "" : "s"} produced by signing.
    </div>

    {downloadError && (
      <div style={{ marginTop: 10, fontSize: 12.5, color: "#DC2626" }}>{downloadError}</div>
    )}

    <div style={{ marginTop: 14 }}>
      {files.map((f) => {
        const Icon = f.icon;
        return (
          <div key={f.name} style={styles.fileRow}>
            <Icon size={16} color={f.color} />
            <div style={styles.fileInfo}>
              <div style={styles.fileName}>{f.name}</div>
              <div style={styles.fileSize}>{f.size}</div>
            </div>
            <button
              style={styles.iconDownloadBtn}
              onClick={() => onDownload(f.path, f.name)}
              title={`Download ${f.name}`}
            >
              <Download size={14} color="#5B4FE0" />
            </button>
          </div>
        );
      })}
    </div>

    <button style={styles.downloadAllBtn} onClick={onDownloadAll}>
      <Download size={14} /> Download All
    </button>
  </div>
);

/* ---------------------------------------------------------
   Main component
--------------------------------------------------------- */

export default function FirmwarePipelineDashboard() {
  const [activeTab, setActiveTab] = useState("CI/CD Pipelines");
  const [activePlatform, setActivePlatform] = useState("github");
  const [activeCodeTab, setActiveCodeTab] = useState("Pipeline template");
  const [downloadError, setDownloadError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const {
    artifactId,
    manifest,
    selectedMethod,
    selectedDigitalId,
    includeTSA,
    anchorBlockchain,
    signature,
    algorithm,
    hashAlgorithm,
    signedAt,
    signResult,
  } = location.state || {};

  const hasData = !!manifest && !!signature;
  const fileName = manifest?.firmware?.file_name || "";

  const PACKAGE_FILES = hasData
    ? buildPackageFiles({ artifactId, manifest, fileName, names: fileNamesFor(selectedDigitalId?.identity_type) })
    : [];

  const SIGNING_DETAILS = buildSigningDetails({
    selectedMethod, algorithm, hashAlgorithm, selectedDigitalId, includeTSA, signResult, signedAt,
  });

  const handleDownload = async (path, downloadName) => {
    setDownloadError("");
    try {
      await downloadWithAuth(path, downloadName);
    } catch (err) {
      setDownloadError(err.message);
    }
  };

  // One after another: browsers block several simultaneous downloads.
  const handleDownloadAll = async () => {
    for (const f of PACKAGE_FILES) {
      await handleDownload(f.path, f.name);
    }
  };

  const snippets = {
    "Pipeline template": () => buildTemplate(activePlatform, { apiUrl: API_URL, digitalId: selectedDigitalId?.id }),
    "Signing script": () => SIGN_SCRIPT,
    "REST API (curl)": () =>
      buildCurlSnippet({ apiUrl: API_URL, fileName, artifactId, selectedDigitalId, includeTSA, anchorBlockchain }),
  };
  const activeSnippet = snippets[activeCodeTab]();

  const handleCopy = () => {
    navigator.clipboard.writeText(activeSnippet);
  };

  const goTab = (tab) => {
    const route = TAB_ROUTES[tab];
    if (route) navigate(route, { state: location.state });
    else setActiveTab(tab);
  };

  return (
    <div style={styles.page}>
      <div style={styles.wrap}>
        <TopTabs activeTab={activeTab} onSelect={goTab} />

        {!hasData && (
          <div style={styles.emptyState}>
            No signed artifact data found. Sign an artifact first, then come back here.
          </div>
        )}

        {hasData && (
          <div style={styles.columns}>
            {/* ---------------- LEFT COLUMN ---------------- */}
            <div style={styles.leftCol}>
              <PlatformCard activePlatform={activePlatform} onSelect={setActivePlatform} />
              <SnippetCard
                activeCodeTab={activeCodeTab}
                onSelectCodeTab={setActiveCodeTab}
                snippet={activeSnippet}
                onCopy={handleCopy}
                onCredentials={() => navigate("/ci-cd-setup")}
              />
              <SigningDetailsCard details={SIGNING_DETAILS} />

              <button style={styles.backBtn} onClick={() => navigate(-1)}>
                <ChevronLeft size={15} /> Back
              </button>
            </div>

            {/* ---------------- RIGHT COLUMN ---------------- */}
            <div style={styles.rightCol}>
              <PackageCard
                files={PACKAGE_FILES}
                downloadError={downloadError}
                onDownload={handleDownload}
                onDownloadAll={handleDownloadAll}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------
   Styles
--------------------------------------------------------- */

const styles = {
  page: {
    width: "100%",
    minHeight: "100vh",
    background: "#fff",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: "#1A1D29",
    boxSizing: "border-box",
    overflowX: "hidden",
  },
  wrap: { width: "100%", maxWidth: 1600, margin: "0 auto", boxSizing: "border-box" },
  topTabs: { display: "flex", gap: 28, borderBottom: "1px solid #E8E9F1", marginBottom: 20, paddingBottom: 0 },
  topTabBtn: { background: "none", border: "none", fontSize: 14, fontWeight: 600, padding: "0 0 12px 0", cursor: "pointer" },
  emptyState: { textAlign: "center", padding: "60px 0", color: "#8B92A8", fontSize: 14 },
  columns: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(300px, 360px)",
    gap: 20,
    alignItems: "start",
    width: "100%",
  },
  leftCol: { display: "flex", flexDirection: "column", gap: 16, minWidth: 0 },
  rightCol: { display: "flex", flexDirection: "column", gap: 16, minWidth: 0 },
  card: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #EDEEF3",
    padding: "20px 22px",
    boxShadow: "0 1px 2px rgba(16,24,40,0.03)",
    boxSizing: "border-box",
    minWidth: 0,
  },
  cardHeaderRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 },
  cardTitle: { fontSize: 15.5, fontWeight: 700, color: "#1A1D29" },
  cardSubtitle: { fontSize: 12.5, color: "#8B92A8", marginTop: 3 },
  cardSubtitleGreen: { fontSize: 12.5, color: "#2E7D32", marginTop: 3 },
  platformGrid: { display: "grid", gridTemplateColumns: "repeat(8, minmax(0, 1fr))", gap: 10, width: "100%" },
  platformTile: {
    position: "relative", background: "#fff", border: "1.5px solid #E8E9F1", borderRadius: 12,
    padding: "12px 8px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
    cursor: "pointer", width: "100%", minWidth: 0, boxSizing: "border-box",
  },
  platformCheck: {
    position: "absolute", top: 5, right: 5, width: 15, height: 15, borderRadius: "50%",
    background: "#5B4FE0", display: "flex", alignItems: "center", justifyContent: "center",
  },
  platformIcon: { width: 40, height: 40, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 700, flexShrink: 0 },
  platformLabel: { fontSize: 11, fontWeight: 600, color: "#3B3F4C", textAlign: "center", lineHeight: 1.3, wordBreak: "break-word" },
  secondaryBtn: {
    display: "flex", alignItems: "center", gap: 6, background: "#fff", border: "1px solid #E0E1EB",
    borderRadius: 8, padding: "8px 14px", fontSize: 12.5, fontWeight: 600, color: "#3B3F4C", cursor: "pointer",
  },
  codeTabsRow: { display: "flex", gap: 20, borderBottom: "1px solid #EDEEF3", marginBottom: 12, flexWrap: "wrap" },
  codeTabBtn: { background: "none", border: "none", fontSize: 12.5, fontWeight: 600, padding: "0 0 10px 0", cursor: "pointer" },
  codeBlock: {
    background: "#111726", borderRadius: 10, padding: "16px 18px",
    fontFamily: "'Fira Code', 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 12.5, lineHeight: "21px", overflowX: "auto", overflowY: "auto", width: "100%", boxSizing: "border-box",
  },
  backBtn: {
    display: "flex", alignItems: "center", gap: 6, alignSelf: "flex-start", background: "#fff",
    border: "1px solid #E0E1EB", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600,
    color: "#3B3F4C", cursor: "pointer",
  },
  fileRow: { display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #F2F3F7" },
  fileInfo: { flex: 1, minWidth: 0 },
  fileName: { fontSize: 12.5, fontWeight: 600, color: "#1A1D29", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  fileSize: { fontSize: 11, color: "#8B92A8" },
  iconDownloadBtn: { background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center" },
  downloadAllBtn: {
    width: "100%", marginTop: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
    background: "#F5F6FA", border: "1px solid #E0E1EB", borderRadius: 8, padding: "10px 0",
    fontSize: 12.5, fontWeight: 600, color: "#5B4FE0", cursor: "pointer", boxSizing: "border-box",
  },
  signingGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 20px", marginTop: 14 },
  signingItem: { display: "flex", alignItems: "flex-start", gap: 10 },
  signingIconWrap: { width: 30, height: 30, borderRadius: 8, background: "#F0EEFF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  signingLabel: { fontSize: 11, color: "#8B92A8", marginBottom: 2 },
  signingValue: { fontSize: 13, fontWeight: 600, color: "#1A1D29" },
};